import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Wallet,
  ShoppingBag,
  ArrowLeftRight,
  Clock,
  CheckCircle2,
  XCircle,
  BadgeCheck,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useApp } from '@/contexts/AppContext';
import { useUserAuth } from '@/contexts/UserAuthContext';
import { useActiveCountries } from '@/hooks/useAppData';
import { useCashbackTransactions, useUserProfile } from '@/hooks/useCashback';
import {
  getCurrencySymbolForCountry,
  getCurrencySymbolForCurrencyCode,
} from '@/utils/formatCurrency';
import { resolveUserCountryCode } from '@/utils/resolveUserCountryCode';
import { CashbackStatus, FirestoreCashbackTransaction } from '@/data/types';
import { CashbackSummaryCard } from '@/components/account/CashbackSummaryCard';

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  CashbackStatus,
  { label: string; icon: React.ElementType; className: string }
> = {
  pending: {
    label: 'قيد مراجعة الأدمن',
    icon: Clock,
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  confirmed: {
    label: 'تمت الإضافة',
    icon: CheckCircle2,
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  rejected: {
    label: 'مرفوض',
    icon: XCircle,
    className: 'bg-red-50 text-red-600 border-red-200',
  },
  paid: {
    label: 'تم الإضافة',
    icon: BadgeCheck,
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
};

function StatusBadge({ status }: { status: CashbackStatus }) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border',
        config.className,
      )}
    >
      <Icon className="w-2.5 h-2.5" />
      {config.label}
    </span>
  );
}

// ─── Date formatter ──────────────────────────────────────────────────────────

function formatDate(ts: unknown): string {
  if (!ts) return '';
  try {
    const date =
      typeof (ts as { toDate?: () => Date }).toDate === 'function'
        ? (ts as { toDate: () => Date }).toDate()
        : new Date(ts as string);
    return date.toLocaleDateString('ar-EG-u-ca-gregory', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// ─── Transaction card ─────────────────────────────────────────────────────────

function TransactionCard({
  tx,
  currencySymbol,
}: {
  tx: FirestoreCashbackTransaction;
  currencySymbol: string;
}) {
  return (
    <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
        <ShoppingBag className="w-5 h-5 text-muted-foreground" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground text-sm truncate">{tx.storeName}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{formatDate(tx.createdAt)}</p>
        <div className="mt-1.5">
          <StatusBadge status={tx.status} />
        </div>
      </div>

      <div className="flex flex-col items-end shrink-0">
        <span className="font-bold text-foreground text-base tabular-nums">
          {(tx.cashbackAmount ?? 0).toFixed(2)}
        </span>
        <span className="text-xs text-muted-foreground">{currencySymbol}</span>
        {tx.orderAmount != null ? (
          <span className="text-[10px] text-muted-foreground/70 mt-0.5 tabular-nums">
            طلب: {Number(tx.orderAmount).toFixed(2)} {currencySymbol}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ─── Discover cashback banner (second block after summary) ─────────────────────

function DiscoverCashbackBanner() {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate('/', { state: { cashbackOnly: true } })}
      className="mx-4 mb-4 cursor-pointer active:scale-[0.98] transition-transform"
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-primary to-primary/80 px-5 py-4 flex items-center gap-4 shadow-md">
        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <ShoppingBag className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">اكتشف عروض الكاش باك</p>
          <p className="text-xs text-white/75 mt-0.5">تسوّق واسترد جزءاً من مدفوعاتك</p>
        </div>
        <div className="shrink-0 bg-white/20 rounded-xl px-3 py-1.5">
          <span className="text-xs font-semibold text-white">تصفّح</span>
        </div>
      </div>
    </div>
  );
}

function OpenReferralBanner() {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate('/cashback/referral')}
      className="mx-4 mb-4 cursor-pointer active:scale-[0.98] transition-transform"
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-primary to-primary/80 px-5 py-4 flex items-center gap-4 shadow-md">
        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <BadgeCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">ادعو صديقك واحصل على كاش باك</p>
          <p className="text-xs text-white/75 mt-0.5">افتح صفحة الإحالة وشارك كودك الآن</p>
        </div>
        <div className="shrink-0 bg-white/20 rounded-xl px-3 py-1.5">
          <span className="text-xs font-semibold text-white">فتح</span>
        </div>
      </div>
    </div>
  );
}

function OpenWalletManagerBanner() {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate('/cashback/wallet')}
      className="mx-4 mb-4 cursor-pointer active:scale-[0.98] transition-transform"
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-primary to-primary/80 px-5 py-4 flex items-center gap-4 shadow-md">
        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <ArrowLeftRight className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-tight">إدارة الرصيد</p>
          <p className="text-xs text-white/75 mt-0.5">
            سحب وتحويل ومراجعة سجل العمليات المالية
          </p>
        </div>
        <div className="shrink-0 bg-white/20 rounded-xl px-3 py-1.5">
          <span className="text-xs font-semibold text-white">فتح</span>
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-8">
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
        <Wallet className="w-10 h-10 text-primary/50" />
      </div>
      <h3 className="text-xl font-bold text-foreground mb-2">لا يوجد كاش باك بعد</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        بعد الشراء من المتجر، يُضاف الرصيد عندما يُستورد طلبك من ملف المبيعات الرسمي
        عبر لوحة التحكم — وليس فور استخدام الكوبون من التطبيق.
      </p>
    </div>
  );
}

// ─── Summary strip ────────────────────────────────────────────────────────────

function SummaryStrip({
  available,
  pending,
  total,
}: {
  available: number;
  pending: number;
  total: number;
}) {
  return (
    <div className="mx-4 mb-4 bg-card rounded-2xl border border-border p-4 grid grid-cols-3 divide-x divide-border rtl:divide-x-reverse">
      <SummaryItem label="متاح" value={available} highlight />
      <SummaryItem label="قيد المراجعة" value={pending} />
      <SummaryItem label="الإجمالي المكتسب" value={total} />
    </div>
  );
}

function SummaryItem({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-col items-center px-2">
      <span
        className={cn(
          'text-lg font-extrabold tabular-nums',
          highlight ? 'text-primary' : 'text-foreground',
        )}
      >
        {value.toFixed(2)}
      </span>
      <span className="text-[10px] text-muted-foreground text-center leading-tight mt-0.5">
        {label}
      </span>
    </div>
  );
}

// ─── Loading skeletons ───────────────────────────────────────────────────────

function LoadingSkeletons() {
  return (
    <div className="px-4 space-y-3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-20 rounded-xl" />
      ))}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function CashbackHistory() {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const { selectedCountry } = useApp();
  const { countries } = useActiveCountries();
  const { transactions, loading: txLoading } = useCashbackTransactions(user?.uid ?? null);
  const { profile, loading: profileLoading } = useUserProfile(user?.uid ?? null);

  const loading = txLoading || profileLoading;
  const summary = profile?.cashbackSummary;
  const confirmedNetFromTransactions = useMemo(
    () =>
      transactions.reduce((sum, tx) => {
        if (tx.status !== 'confirmed' && tx.status !== 'paid') return sum;
        const amount = Number(tx.cashbackAmount) || 0;
        const signed =
          tx.source === 'admin_adjustment' && tx.adjustmentSign === -1
            ? -amount
            : amount;
        return sum + signed;
      }, 0),
    [transactions],
  );
  const totalEarnedFromTransactions = useMemo(
    () =>
      transactions.reduce((sum, tx) => {
        if (tx.status !== 'confirmed' && tx.status !== 'paid') return sum;
        const amount = Number(tx.cashbackAmount) || 0;
        if (tx.source === 'admin_adjustment') {
          return tx.adjustmentSign === -1 ? sum : sum + amount;
        }
        return sum + amount;
      }, 0),
    [transactions],
  );
  const pendingFromTransactions = useMemo(
    () =>
      transactions
        .filter((tx) => tx.status === 'pending')
        .reduce((sum, tx) => sum + (Number(tx.cashbackAmount) || 0), 0),
    [transactions],
  );
  const effectivePending = Math.max(
    summary?.pendingBalance ?? 0,
    pendingFromTransactions,
  );
  const effectiveAvailable = Math.max(
    summary?.availableBalance ?? 0,
    confirmedNetFromTransactions,
  );
  const effectiveTotalEarned = Math.max(
    summary?.totalEarned ?? 0,
    totalEarnedFromTransactions,
  );
  const effectiveSummary = summary
    ? {
        ...summary,
        availableBalance: effectiveAvailable,
        pendingBalance: effectivePending,
        totalEarned: effectiveTotalEarned,
      }
    : summary;

  const currencySymbol = useMemo(() => {
    if (summary?.currencyCode) {
      return getCurrencySymbolForCurrencyCode(summary.currencyCode);
    }

    const code = resolveUserCountryCode(profile, selectedCountry, countries);
    return getCurrencySymbolForCountry(code);
  }, [profile, selectedCountry, countries, summary?.currencyCode]);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      {/* Header — fixed height strip above scroll */}
      <div className="flex items-center justify-between gap-3 px-4 pt-12 pb-5 shrink-0">
        <h1 className="text-xl font-bold text-foreground">الكاش باك</h1>
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 active:opacity-90 transition-opacity ring-2 ring-white/20 shadow-sm"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {loading ? (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
          <LoadingSkeletons />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
          <CashbackSummaryCard summary={effectiveSummary} currencySymbol={currencySymbol} />

          <DiscoverCashbackBanner />

          <OpenReferralBanner />
          <OpenWalletManagerBanner />

          {/* Summary strip — only when there is data */}
          {transactions.length > 0 && (
            <SummaryStrip
              available={effectiveAvailable}
              pending={effectivePending}
              total={effectiveTotalEarned}
            />
          )}

          {/* Transactions list or empty state */}
          {transactions.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="px-4 pb-6 space-y-3">
              {transactions.map((tx) => (
                <TransactionCard key={tx.id} tx={tx} currencySymbol={currencySymbol} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
